import React, { useState, useEffect } from "react";
import * as gtag from "@modules/gtag";

import NavBar from "@landing-components/NavBar/NavBar";
import Footer from "@landing-components/Footer/Footer";
import SEO from "@components/seo";
import Dialog from "@components/Dialog/Dialog";
import Button from "@components/Button/Button";
import Input, { emailValidator } from "@components/Input/Input";

import "./pricing.scss";

const PricingPage = (props) => {
    const [showRequestAccess, setShowRequestAccess] = useState(false);
    const [pricingPlanClicked, setPricingPlanClicked] = useState();
    const [hasRequestedAccess, setHasRequestedAccess] = useState(false);
    const [submittingBetaRequest, setSubmittingBetaRequest] = useState(false);
    const [email, setEmail] = useState();
    const [errorMessage, setErrorMessage] = useState({ main: "", sub: "" });

    function handleSignupClick(type) {
        gtag.event({
            action: "click",
            category: "Pricing Page",
            label: type,
        });

        setPricingPlanClicked(type);
        setShowRequestAccess(true);
    }

    async function handleRequestAccessClick(e) {
        e.preventDefault();
        if (!email) return;

        setSubmittingBetaRequest(true);

        gtag.event({
            action: "submit_form",
            category: "Pricing Page",
            label: "request beta access",
        });

        const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/beta-access`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ email, planRequested: pricingPlanClicked }),
        });

        if (response.status === 409) {
            setErrorMessage({
                main: "Access already requested",
                sub: "I respect how keen you are. Expect an email very soon!",
            });
        }

        setHasRequestedAccess(true);
        setSubmittingBetaRequest(false);
    }

    useEffect(() => {
        if (hasRequestedAccess) {
            setTimeout(() => {
                setShowRequestAccess(false);
            }, 5000);
        }
    }, [hasRequestedAccess]);

    function handleCloseDialog() {
        setEmail();
        setErrorMessage({ main: "", sub: "" });
        setHasRequestedAccess(false);
        setShowRequestAccess(false);
    }

    return (
        <div className="pricing-page text-block">
            <SEO type="corporation" metadata={{ title: "Scribe - A Second Brain for you and your team" }} />

            <NavBar theme="light" />

            <div className="pricing-page-content">
                <div className="banner-container">
                    <div className="banner">
                        <h1>A plan for everyone</h1>
                    </div>
                    {/* <p className="banner-subtext">No credit card required for the team trial</p> */}
                </div>

                <section className="pricing-cards-container">
                    <ul className="pricing-cards">
                        <li className="pricing-card" data-type="personal">
                            <div className="pricing-card-classification">Personal</div>
                            <div className="pricing-card-content">
                                <p className="pricing-card-description">
                                    A powerful note taking tool unlike any other
                                </p>
                                <div className="pricing-card-price">Free</div>
                                <button
                                    className="pricing-card-sign-up-btn"
                                    onClick={() => handleSignupClick("personal")}
                                >
                                    Sign me up
                                </button>
                                <ul className="pricing-card-features">
                                    <li>Unlimited pages</li>
                                    <li>Unlimited workspaces</li>
                                    <li>One member only</li>
                                </ul>
                            </div>
                        </li>
                        <li className="pricing-card" data-type="team">
                            <div className="pricing-card-classification">Team</div>
                            <div className="pricing-card-content">
                                <p className="pricing-card-description">
                                    A collaborative knowledge hub that can scale to any size
                                </p>
                                <div className="pricing-card-price">
                                    <span>$15</span>
                                    <span className="pricing-card-price-info">
                                        <div>per person</div>
                                        <div>per month</div>
                                    </span>
                                </div>
                                <button
                                    className="pricing-card-sign-up-btn"
                                    onClick={() => handleSignupClick("team")}
                                >
                                    Sign me up
                                </button>
                                <ul className="pricing-card-features">
                                    <li>Unlimited pages</li>
                                    <li>Unlimited workspaces</li>
                                    <li>Unlimited members</li>
                                    <li>Feed page</li>
                                    <li>Task management</li>
                                    <li>Priority Support</li>
                                </ul>
                            </div>
                        </li>

                        <li className="pricing-card" data-type="enterprise">
                            <div className="pricing-card-classification">Enterprise</div>
                            <div className="pricing-card-content">
                                <p className="pricing-card-description">
                                    The same magic whilst managing risk, security, and compliance
                                </p>
                                <div className="pricing-card-price">Contact Us</div>
                                <a className="contact-us-email" href="mailto: hello@scribe.wiki">
                                    hello@scribe.wiki
                                </a>
                                <ul className="pricing-card-features">
                                    <li>All team features</li>
                                    <li>Unlimited version history</li>
                                    <li>Dedicated support</li>
                                    <li>Custom Auth</li>

                                    <li>On Premise</li>
                                </ul>
                            </div>
                        </li>
                    </ul>
                </section>
            </div>

            <Dialog show={showRequestAccess} onClose={handleCloseDialog} ariaLabel="Request Beta Access">
                <div className="request-access-dialog">
                    <h2 className="add-members-dialog-title">Request Beta Access</h2>
                    <p className="add-members-dialog-explanation">
                        Scribe is currently in beta whilst we finish adding in the team features and polishing
                        the editor. If you want to be a part of the journey to a 1.0 release and beyond we
                        would love to have you. Just enter your email and we will get back to you ASAP!
                    </p>
                    <form
                        className="signup-form"
                        onSubmit={handleRequestAccessClick}
                        data-requested-access={hasRequestedAccess}
                    >
                        <div className="input-container">
                            <Input
                                label="Enter email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                type="email"
                                id="request-access-email"
                            />
                        </div>
                        <div className="submit-btn-container">
                            <Button
                                variant="primary"
                                size="medium"
                                loading={submittingBetaRequest}
                                disabled={!email}
                            >
                                Request Access
                            </Button>
                        </div>

                        <div className="thank-you">
                            <p>{errorMessage.main || "Thank you"}</p>
                            <p>{errorMessage.sub || "Expect an email very soon"}</p>
                        </div>
                    </form>
                </div>
            </Dialog>

            <Footer />
        </div>
    );
};

export default PricingPage;
